import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../lib/setupAxiosAuth';
import { createInitialProgress } from './useCrawlFormState';
import { pollCrawlJobUntilDone } from './useCrawlJobPolling';

const API = '/api';
const RECURSIVE_CRAWL_ENDPOINT = `${API}/documents/crawl-recursive-stream`;

const AUTH_FIELD_MAP = {
  credentials: (c) => ({ username: c.username || null, password: c.password || null }),
  google: (c) => ({
    google_email: c.googleEmail || null,
    google_password: c.googlePassword || null,
    remember_google_session: c.rememberGoogleSession,
    use_saved_google_session: c.useSavedGoogleSession,
  }),
  cookies: () => ({}),
};

function buildAuthPayload(config) {
  const modeFields = (AUTH_FIELD_MAP[config.authMode] || AUTH_FIELD_MAP.cookies)(config);
  return {
    auth_mode: config.authMode,
    session_cookies: config.sessionCookies || null,
    username: null, password: null,
    google_email: null, google_password: null,
    remember_google_session: false, use_saved_google_session: false,
    ...modeFields,
  };
}

function buildRecursivePayload(crawlConfig) {
  return {
    knowledge_base_id: crawlConfig.knowledgeBaseId,
    start_url: crawlConfig.url,
    max_pages: crawlConfig.maxPages,
    url_prefix: crawlConfig.urlPrefix || null,
    force_recrawl: crawlConfig.forceRecrawl || false,
    ...buildAuthPayload(crawlConfig),
  };
}

function getStreamEventData(eventChunk) {
  const dataLine = eventChunk.split('\n').find((line) => line.startsWith('data: '));
  return dataLine ? JSON.parse(dataLine.slice(6)) : null;
}

function mergeProgressState(current, data, maxPages) {
  return {
    ...current,
    ...data,
    current_page: data.current_page ?? current?.current_page ?? 0,
    max_pages: data.max_pages ?? current?.max_pages ?? maxPages,
    pages_found: data.pages_found ?? current?.pages_found ?? 0,
    duplicates_found: data.duplicates_found ?? current?.duplicates_found ?? 0,
    pages_skipped: data.pages_skipped ?? current?.pages_skipped ?? 0,
    status: data.status ?? current?.status ?? 'En cours...',
  };
}

async function openRecursiveCrawlStream(crawlPayload) {
  const response = await fetch(RECURSIVE_CRAWL_ENDPOINT, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(crawlPayload),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    const detail = errorBody ? JSON.parse(errorBody)?.detail : null;
    throw new Error(detail || `Erreur serveur (${response.status})`);
  }

  if (!response.body) {
    throw new Error('Le navigateur ne supporte pas le streaming SSE');
  }

  return response.body.getReader();
}

function handleStreamProgress(data, progressHandlers, maxPages) {
  progressHandlers.setProgress((current) => mergeProgressState(current, data, maxPages));
}

function handleStreamComplete(data, progressHandlers) {
  progressHandlers.setCompletedResult(data);
  progressHandlers.setCrawling(false);
}

function dispatchStreamEvent(data, maxPages, progressHandlers, jobIdRef) {
  if (data.type === 'start') {
    jobIdRef.current = data.job_id || data.crawl_id;
    return 'continue';
  }
  if (data.type === 'heartbeat') {
    return 'continue';
  }
  if (data.type === 'progress') {
    handleStreamProgress(data, progressHandlers, maxPages);
    return 'continue';
  }
  if (data.type === 'complete') {
    handleStreamComplete(data, progressHandlers);
    return 'complete';
  }
  if (data.type === 'error') {
    throw new Error(data.message || 'Erreur lors du crawling');
  }
  return 'continue';
}

function processEventBuffer(buffer, done) {
  const events = buffer.split('\n\n');
  const leftover = done ? '' : (events.pop() || '');
  return { events, leftover };
}

async function readStreamEvents(reader, maxPages, progressHandlers, jobIdRef) {
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: !done });

    const { events, leftover } = processEventBuffer(buffer, done);
    buffer = leftover;

    for (const eventChunk of events) {
      const data = getStreamEventData(eventChunk);
      if (!data) continue;
      if (dispatchStreamEvent(data, maxPages, progressHandlers, jobIdRef) === 'complete') {
        return true;
      }
    }

    if (done) return false;
  }
}

function useRecursiveProgress(progressHandlers, maxPages) {
  return useCallback(() => {
    progressHandlers.setProgress(createInitialProgress(maxPages));
  }, [maxPages, progressHandlers]);
}

export function useRecursiveCrawlStream(crawlConfig, progressHandlers) {
  const crawlPayload = useMemo(() => buildRecursivePayload(crawlConfig), [crawlConfig]);
  const initializeProgress = useRecursiveProgress(progressHandlers, crawlConfig.maxPages);
  const openStream = useCallback(() => openRecursiveCrawlStream(crawlPayload), [crawlPayload]);

  return useCallback(async () => {
    toast.info(`Crawl récursif en cours (max ${crawlConfig.maxPages} pages)... Cela peut prendre plusieurs minutes.`);
    initializeProgress();
    const reader = await openStream();
    const jobIdRef = { current: null };
    const completed = await readStreamEvents(reader, crawlConfig.maxPages, progressHandlers, jobIdRef);
    if (completed) {
      return true;
    }
    if (jobIdRef.current) {
      toast.info('Connexion interrompue — reprise du suivi en arrière-plan...');
      return await pollCrawlJobUntilDone(jobIdRef.current, progressHandlers);
    }
    return false;
  }, [crawlConfig.maxPages, initializeProgress, openStream, progressHandlers]);
}
